interface PieChartProps {
  data: Array<{ label: string; value: number; color?: string }>;
  total: number;
}

const COLORS = [
  '#3B82F6', // blue-500
  '#10B981', // green-500
  '#F59E0B', // amber-500
  '#EF4444', // red-500
  '#8B5CF6', // violet-500
  '#EC4899', // pink-500
  '#6366F1' // indigo-500
];

export function PieChart({ data, total }: PieChartProps) {
  if (total === 0 || data.length === 0) {
    return (
      <div className='flex h-64 items-center justify-center text-gray-400 dark:text-gray-500'>
        No data available
      </div>
    );
  }

  const radius = 80;
  const centerX = 100;
  const centerY = 100;

  // データが1つだけで100%の場合は円を描画
  if (data.length === 1 && data[0].value === total) {
    const color = data[0].color ?? COLORS[0];
    const percentage = 100;

    return (
      <div className='flex flex-col items-center'>
        <svg viewBox='0 0 200 200' className='h-64 w-64'>
          <circle
            cx={centerX}
            cy={centerY}
            r={radius}
            fill={color}
            stroke='white'
            strokeWidth='2'
            className='dark:stroke-gray-800'
          />
        </svg>

        <div className='mt-4 w-full space-y-2'>
          <div className='flex items-center justify-between text-sm'>
            <div className='flex items-center gap-2'>
              <div className='h-3 w-3 rounded-sm' style={{ backgroundColor: color }} />
              <span className='font-medium text-gray-700 dark:text-gray-200'>{data[0].label}</span>
            </div>
            <div className='flex items-center gap-2'>
              <span className='text-gray-600 dark:text-gray-300'>
                {data[0].value.toLocaleString()}
              </span>
              <span className='font-semibold text-gray-800 dark:text-white'>
                ({percentage.toFixed(1)}%)
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  let currentAngle = -90; // Start from top

  const slices = data.map((item, index) => {
    const percentage = (item.value / total) * 100;
    const angle = (item.value / total) * 360;

    // Calculate arc path
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;

    const startX = centerX + radius * Math.cos((startAngle * Math.PI) / 180);
    const startY = centerY + radius * Math.sin((startAngle * Math.PI) / 180);
    const endX = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
    const endY = centerY + radius * Math.sin((endAngle * Math.PI) / 180);

    const largeArcFlag = angle > 180 ? 1 : 0;

    const pathData = [
      `M ${centerX} ${centerY}`,
      `L ${startX} ${startY}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
      'Z'
    ].join(' ');

    currentAngle += angle;

    return {
      pathData,
      color: item.color ?? COLORS[index % COLORS.length],
      label: item.label,
      value: item.value,
      percentage: percentage.toFixed(1)
    };
  });

  return (
    <div className='flex flex-col items-center'>
      <svg viewBox='0 0 200 200' className='h-64 w-64'>
        {slices.map((slice, index) => (
          <path
            key={index}
            d={slice.pathData}
            fill={slice.color}
            stroke='white'
            strokeWidth='2'
            className='dark:stroke-gray-800'
          />
        ))}
      </svg>

      <div className='mt-4 w-full space-y-2'>
        {slices.map((slice, index) => (
          <div key={index} className='flex items-center justify-between text-sm'>
            <div className='flex items-center gap-2'>
              <div className='h-3 w-3 rounded-sm' style={{ backgroundColor: slice.color }} />
              <span className='font-medium text-gray-700 dark:text-gray-200'>{slice.label}</span>
            </div>
            <div className='flex items-center gap-2'>
              <span className='text-gray-600 dark:text-gray-300'>
                {slice.value.toLocaleString()}
              </span>
              <span className='font-semibold text-gray-800 dark:text-white'>
                ({slice.percentage}%)
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
